import jsPDF from "jspdf";
import "jspdf-autotable";
import { CurrentTime, currentDate_dmy } from "../../components/Common/CommonFunction";

// ---------- helpers ----------

// Pick the first available key from the API row (API field names can differ slightly)
const pick = (row, keys, fallback = "") => {
    for (const k of keys) {
        if (row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k];
    }
    return fallback;
};

// yyyy-mm-dd  ->  dd-mm-yyyy
const fmtDate = (d) => {
    if (!d) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(d);
};

// "2026-08" -> { monthName: "August", year: "2026" }  (safe if month is missing)
const monthAndYear = (selectedMonth) => {
    if (!selectedMonth || !String(selectedMonth).includes("-")) return { monthName: "", year: "" };
    const [year, month] = String(selectedMonth).split("-");
    const monthName = new Date(Number(year), Number(month) - 1, 1).toLocaleString("default", { month: "long" });
    return { monthName, year };
};

// ---------- page parts (same structure as the Loyalty report) ----------

const pageBorder = (doc) => {
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor("black");
    doc.setLineWidth(0.8);
    doc.line(w - 30, 17, 30, 17);         // Top
    doc.line(30, h - 20, 30, 17);         // Left
    doc.line(w - 30, h - 20, w - 30, 17); // Right
    doc.line(w - 30, h - 20, 30, h - 20); // Bottom
};

const pageTitle = (doc, title) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - textWidth) / 2, 42);
};

const pageFooter = (doc, title) => {
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        pageBorder(doc);
        pageTitle(doc, title);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        const footerY = pageHeight - 8;
        doc.text(`Print Date: ${currentDate_dmy}  Time: ${CurrentTime()}`, 30, footerY);
        const pageText = `Page ${i} of ${pageCount}`;
        doc.text(pageText, pageWidth - 30 - doc.getTextWidth(pageText), footerY);
    }
};

// ---------- main ----------

const VoucherRedemptionCouponPDF = (resp) => {
    try {
        const list = Array.isArray(resp?.Data) ? resp.Data : [];

        if (list.length === 0) {
            alert("No data found to generate the report!");
            return;
        }

        const { monthName, year } = monthAndYear(resp.Month);
        const title = `Generic Gift Voucher Claim Summary - ${monthName} ${year}`.trim();

        const doc = new jsPDF("p", "pt", "a4");

        const columns = ["Invoice Date", "Month", "Party Name", "Scheme Name", "Voucher Code", "DiscountAmount"];

        // ----- rows -----
        let grandTotal = 0;
        const rows = list.map((row) => {
            const amt = Number(pick(row, ["DiscountAmount", "Discount", "Amount"], 0)) || 0;
            grandTotal += amt;
            return [
                fmtDate(pick(row, ["InvoiceDate", "Date"])),
                monthName,
                pick(row, ["PartyName", "Party", "Name"]),
                pick(row, ["SchemeName", "Scheme"]),
                pick(row, ["VoucherCode", "VoucherNo", "CouponCode"]),
                amt,
            ];
        });

        // Grand Total row
        rows.push([
            { content: "Grand Total", colSpan: 5, styles: { fontStyle: "bold", halign: "left", fontSize: 8 } },
            { content: grandTotal, styles: { fontStyle: "bold", halign: "right", fontSize: 8 } },
        ]);

        // Signature section
        rows.push([
            { content: "Franchise Stamp & Signature :", colSpan: 4, styles: { halign: "left", fontStyle: "bold", fontSize: 8, minCellHeight: 40 } },
            { content: "Date :", colSpan: 2, styles: { halign: "left", fontStyle: "bold", fontSize: 8, minCellHeight: 40 } },
        ]);
        rows.push([
            { content: "Sales Team Signature :", colSpan: 4, styles: { halign: "left", fontStyle: "bold", fontSize: 8, minCellHeight: 40 } },
            { content: "", colSpan: 2, styles: { minCellHeight: 40 } },
        ]);

        // ----- table -----
        doc.autoTable(columns, rows, {
            margin: { left: 30, right: 30, top: 55, bottom: 30 },
            startY: 55,
            theme: "grid",
            styles: { overflow: "linebreak", fillColor: [255, 255, 255] },
            headStyles: {
                cellPadding: 3,
                lineWidth: 0.8,
                valign: "middle",
                fontStyle: "bold",
                halign: "left",
                fillColor: "white",
                textColor: [0, 0, 0],
                fontSize: 8,
                lineColor: [0, 0, 0],
            },
            bodyStyles: {
                textColor: [30, 30, 30],
                cellPadding: 2,
                fontSize: 7.5,
                lineColor: [0, 0, 0],
                fillColor: [255, 255, 255],
            },
            columnStyles: {
                0: { columnWidth: 62, halign: "left" },
                1: { columnWidth: 45, halign: "left" },
                2: { columnWidth: 125, halign: "left" },
                3: { columnWidth: 135, halign: "left" },
                4: { columnWidth: 98, halign: "left" },
                5: { columnWidth: 70, halign: "right" },
            },
            tableLineColor: "black",
            didParseCell: (data) => {
                // keep the last column header right aligned like the amounts below it
                if (data.section === "head" && data.column.index === 5) {
                    data.cell.styles.halign = "right";
                }
            },
        });

        pageFooter(doc, title);

        doc.setProperties({ title });

        // Open PDF in a new tab (no download)
        const pdfBlob = doc.output("blob");
        window.open(URL.createObjectURL(pdfBlob));
    } catch (error) {
        console.error("Error generating Voucher Redemption Coupon report:", error);
    }
};

export default VoucherRedemptionCouponPDF;